import { createRouter, createWebHistory } from "vue-router";
import store from "../store";
import mixpanel from "../../api/mixpanelInstance";
import { afterLoginLocation, returnToQuery } from "./returnTo";

const routeToSubscription = (to, next) => {
  const user = store.getters["sessions/user"];

  let routeName = null;
  switch (to.name) {
    case "new-subscription":
      if (user.subscription_purchase_role === "guardian") {
        routeName = "new-guardian-subscription";
      }
      break;
    case "new-guardian-subscription":
      if (user.subscription_purchase_role !== "guardian") {
        routeName = "new-subscription";
      }
      break;
    case "edit-subscription":
      if (!user.active_subscriber) {
        routeName = "new-subscription";
      } else if (user.subscription_purchase_role === "guardian") {
        routeName = "edit-guardian-subscription";
      }
      break;
    case "edit-guardian-subscription":
      if (!user.active_subscriber) {
        routeName = "new-guardian-subscription";
      } else if (user.subscription_purchase_role !== "guardian") {
        routeName = "edit-subscription";
      }
      break;
  }

  if (routeName) {
    next({ name: routeName, query: to.query, hash: to.hash });
  } else {
    next();
  }
};

const router = createRouter({
  history: createWebHistory("/"),
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import("../views/Login.vue"),
      beforeEnter: (to, from, next) => {
        store.dispatch("sessions/loginUserWithToken").then((response) => {
          if (response?.status < 300) {
            next(afterLoginLocation(to.query.return_to, router));
          } else {
            next();
          }
        });
      },
    },
    {
      path: "/subscriptions/new",
      name: "new-subscription",
      component: () => import("../views/NewSubscriptionView.vue"),
      meta: {
        requiresAuth: true,
      },
      beforeEnter: (to, from, next) => {
        routeToSubscription(to, next);
      },
    },
    {
      path: "/subscriptions/guardian/new",
      name: "new-guardian-subscription",
      component: () => import("../views/NewGuardianSubscriptionView.vue"),
      meta: {
        requiresAuth: true,
      },
      beforeEnter: (to, from, next) => {
        routeToSubscription(to, next);
      },
    },
    {
      path: "/subscriptions/guardian/edit",
      name: "edit-guardian-subscription",
      component: () => import("../views/EditGuardianSubscriptionView.vue"),
      meta: {
        requiresAuth: true,
      },
      beforeEnter: (to, from, next) => {
        routeToSubscription(to, next);
      },
    },
    {
      path: "/subscriptions/edit",
      name: "edit-subscription",
      component: () => import("../views/EditSubscriptionView.vue"),
      meta: {
        requiresAuth: true,
      },
      beforeEnter: (to, from, next) => {
        routeToSubscription(to, next);
      },
    },
    {
      path: "/payment-confirmation/:transactionId",
      name: "payment-confirmation",
      props: true,
      component: () => import("../views/PaymentConfirmation.vue"),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/signup",
      name: "signup",
      component: () => import("../views/SignupView.vue"),
    },
    {
      path: "/forgot-password",
      name: "forgot-password",
      component: () => import("../views/ForgotPasswordView.vue"),
    },
    {
      path: "/reset-password",
      name: "reset-password",
      component: () => import("../views/ResetPasswordView.vue"),
    },
    {
      path: "/cancel-plan",
      name: "cancel-plan",
      component: () => import("../views/CancelPlanView.vue"),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/delete-account",
      name: "delete-account",
      component: () => import("../views/DeleteAccountView.vue"),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/account-setup",
      name: "account-setup",
      component: () => import("../views/AccountSetupView.vue"),
      meta: {
        requiresAuth: true,
      },
    },
    {
      path: "/",
      name: "Home",
      component: () => import("../views/MainLayoutView.vue"),
      children: [
        {
          path: "",
          name: "dashboard",
          component: () => import("../views/Dashboard.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "curriculum",
          name: "curriculum",
          component: () => import("../views/CurriculumView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "implementation-quiz",
          name: "implementation-quiz",
          component: () => import("../views/ImplementationQuizView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "lesson-plan",
          name: "implementation-plan",
          component: () => import("../views/ImplementationPlanView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "lesson-plan/:id",
          name: "lesson-plan",
          component: () => import("../views/LessonPlanView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "lesson-plan/:lessonPlanId/lessons/:id",
          name: "lesson",
          component: () => import("../views/LessonView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "modules",
          name: "module-library",
          component: () => import("../views/ModuleLibrary.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "modules/:id",
          name: "module",
          component: () => import("../views/ModuleView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "news-resources",
          name: "news-resources",
          component: () => import("../views/NewsResources.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "help-and-guides",
          name: "help-and-guides",
          component: () => import("../views/HelpAndGuides.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "students/:id",
          name: "student",
          component: () => import("../views/StudentView.vue"),
          meta: {
            requiresAuth: true,
          },
          beforeEnter: (to, from, next) => {
            store
              .dispatch("students/loadStudent", to.params.id)
              .then((response) => {
                if (response.status < 300) {
                  next();
                } else {
                  store.dispatch("showToast", {
                    type: "error",
                    message: "Student not found",
                  });
                  next({ name: "dashboard" });
                }
              });
          },
        },
        {
          path: "students/:id/activity-history",
          name: "student-activity-history",
          component: () => import("../views/StudentActivityHistoryView.vue"),
          meta: {
            requiresAuth: true,
          },
          beforeEnter: (to, from, next) => {
            if (!to.params.id || to.params.id === "undefined") {
              next({ name: "dashboard" });
              return;
            }
            next();
          },
        },
        {
          path: "students/:id/streaming",
          name: "streaming",
          component: () => import("../views/StreamingView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "classroom/:id",
          name: "classroom",
          component: () => import("../views/ClassRoomView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "archive/:classId",
          name: "archive",
          component: () => import("../views/StudentArchiveView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "group-dashboard",
          name: "group-dashboard",
          component: () => import("../views/GroupManagementView.vue"),
          meta: {
            requiresAuth: true,
            requiresSchoolAdmin: true,
          },
        },
        {
          path: "group-dashboard/analytics",
          redirect: { name: "group-dashboard", query: { tab: "sessions" } },
        },
        {
          path: "group-dashboard/advisors/:id",
          name: "group-advisor",
          component: () => import("../views/GroupAdvisorDetailView.vue"),
          meta: {
            requiresAuth: true,
            requiresSchoolAdmin: true,
          },
        },
        {
          path: "group-dashboard/students/:id",
          name: "group-student",
          component: () => import("../views/GroupStudentDetailView.vue"),
          meta: {
            requiresAuth: true,
            requiresSchoolAdmin: true,
          },
        },
        {
          path: "group-dashboard/classrooms/:id",
          name: "group-classroom",
          component: () => import("../views/GroupClassroomDetailView.vue"),
          meta: {
            requiresAuth: true,
            requiresSchoolAdmin: true,
          },
        },
        {
          path: ":pathMatch(.*)*",
          name: "not-found",
          component: () => import("../views/NotFoundView.vue"),
          meta: {
            requiresAuth: true,
          },
        },
      ],
    },
  ],
});

router.beforeEach((to, from, next) => {
  if (to.query.google_error === 'email_mismatch') {
    store.dispatch('showToast', {
      type: 'error',
      message: "Could not connect Google account — please make sure your account emails match.",
      duration: 8000,
    });
    const query = { ...to.query };
    delete query.google_error;
    return next({ ...to, query, replace: true });
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    store.dispatch("sessions/loginUserWithToken").then((response) => {
      if (response && response.status < 300) {
        const user = store.getters["sessions/user"];
        mixpanel.identify(user.id);
        // if user is not signed up, redirect to account-setup
        if (!user.signed_up && to.name !== "account-setup") {
          next({ name: "account-setup", query: returnToQuery(to.fullPath, router) });
        } else if (user.signed_up && to.name === "account-setup") {
          next(afterLoginLocation(to.query.return_to, router));
        } else if (to.matched.some((record) => record.meta.requiresSchoolAdmin)) {
          if (store.getters["sessions/isSchoolAdmin"]) {
            next();
          } else {
            next({ name: "dashboard" });
          }
        } else {
          next();
        }
      } else {
        const destination = to.name === "account-setup" ? to.query.return_to : to.fullPath;
        next({ name: "login", query: returnToQuery(destination, router) });
      }
    });
  } else {
    next();
  }
});

export default router;
